import {
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, of, switchMap } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../../core/api/api.config';
import {
  FrontendBlog,
  FrontendBlogComment,
  FrontendBlogContentWidget,
  FrontendStore,
  FrontendUser,
} from '../../../../core/api/api.models';
import { blogReadMinutes } from '../../../../core/api/blog-text';
import { AssetUrlPipe } from '../../../../core/api/asset-url.pipe';
import { SavingArenaApiService } from '../../../../core/api/saving-arena-api.service';
import { TrustedCmsHtmlPipe } from '../../../../core/api/trusted-cms-html.pipe';
import { NewsletterSignup } from '../../../../core/newsletter/newsletter-signup';
import { SeoService } from '../../../../core/seo/seo.service';
import { SsrResponseService } from '../../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../../core/seo/breadcrumb-trail';
import { articleSchema, faqPageSchema } from '../../../../core/seo/structured-data';
import { Breadcrumb } from '../../../../shared/breadcrumb/breadcrumb';
import { CouponCard } from '../../../../shared/coupon-card/coupon-card';
import { toCouponCardView } from '../../../../shared/coupon-card/coupon-card.mapper';
import { CouponCardView } from '../../../../shared/coupon-card/coupon-card.models';
import { ProductCard } from '../../../../shared/product-card/product-card';
import { EventTopStores } from '../../../../shared/event-top-stores/event-top-stores';
import { SectionHeading } from '../../../../shared/section-heading/section-heading';
import { stripHtml, toBlogPosts } from '../../blog-post.mapper';
import { BlogPost } from '../../blog.models';
import { AffiliateLinkService } from '../../../../core/affiliate/affiliate-link.service';

/** Cards in the "Alternative Stores" grid — three rows of the existing two-column layout. */
const RELATED_STORE_LIMIT = 6;

/** Cards in the "Related Blogs" strip under the article — one row of four. */
const RELATED_POST_LIMIT = 4;

/** A store assigned to this post, rendered as an alternative-store card. */
interface RelatedStore {
  id: string;
  name: string;
  path: string;
  domain: string;
  description: string;
  logoUrl: string;
}

/**
 * The "Written by" sidebar card. Same shape as the store page's `PageEditor`, because both cards
 * render the same design from the same backend payload (`serializeStoreEditor`).
 */
interface ArticleAuthor {
  name: string;
  role: string;
  bio: string;
  avatarPath: string;
  initials: string;
  profileUrl: string;
}

/** "SavingArena Admin" -> "SA". Only used when an author has no profile image. */
function authorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'SA';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Caps the card bio; "Read more" links to the author page for the rest. */
function truncateWords(text: string, maxWords: number): string {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return text.trim();
  return `${words.slice(0, maxWords).join(' ')}…`;
}

/** An author in the "SavingArena Heroes" strip. */
interface HeroAuthor {
  slug: string;
  name: string;
  role: string;
  imageUrl: string;
  profilePath: string;
}

/**
 * Rejects anything that reads as a link. Mirrors `containsUrl` in the backend route — this copy is
 * only so the visitor is told before a round trip; the server is what actually enforces it.
 */
const URL_PATTERNS: RegExp[] = [
  /\b(?:https?|ftp|hxxps?):\/\//i,
  /\bwww\d{0,3}\./i,
  /\b[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9-]{1,63})*\.(?:com|net|org|io|co|uk|de|fr|es|it|nl|ru|in|pk|au|ca|us|info|biz|shop|store|online|site|xyz|top|link|click|app|dev|me|tv|cc)\b/i,
  /\(\s*dot\s*\)|\[\s*dot\s*\]|\s+dot\s+[a-z]{2,}/i,
  /<\s*a\b|\[url|\[link/i,
];

function containsUrl(value: string): boolean {
  return URL_PATTERNS.some((pattern) => pattern.test(value || ''));
}

interface TocChildItem {
  id: string;
  text: string;
}

interface TocGroupItem {
  id: string;
  text: string;
  children: TocChildItem[];
}

interface ProcessedArticle {
  /** The content HTML with an `id` injected on every heading, so the TOC can anchor to it. */
  html: string;
  toc: TocGroupItem[];
}

function stripTags(value: string): string {
  return value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function slugifyHeading(text: string, index: number): string {
  const slug = text
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `${slug}-${index}` : `section-${index}`;
}

/**
 * Injects an `id` onto every h2-h6 in the article HTML and builds the table of contents from the
 * same pass.
 *
 * Done as a STRING transform rather than by reading the DOM after render, so it runs identically on
 * the server and the client: the processed HTML and the TOC are both part of the SSR output, which
 * means the TOC is crawlable and there is no post-hydration DOM-walking flicker.
 */
/**
 * One piece of the rendered article: either a run of CMS HTML or a widget between two of them.
 *
 * The body can no longer be a single `[innerHTML]`: a coupon widget has to be a real Angular
 * component so its reveal flow works, and components cannot be created inside an innerHTML string.
 * So the body is split into top-level blocks and the widgets are interleaved between them.
 */
export type ArticleBlock =
  | { kind: 'html'; id: string; html: string }
  | { kind: 'widget'; id: string; widget: FrontendBlogContentWidget }
  // Two or more product widgets placed back to back at one spot. Rendered as one grid row instead
  // of a column of single 320px cards, which pushed the next paragraph a full screen down per card.
  | { kind: 'products'; id: string; widgets: FrontendBlogContentWidget[] };

/**
 * Splits CMS HTML into its top-level block elements.
 *
 * Depth-tracked rather than split on `</p>`: admin content nests (a `<ul>` inside a `<blockquote>`),
 * and splitting on a closing tag would cut a list in half. Trailing text outside any element is
 * kept, so nothing in the article is silently dropped.
 */
function splitTopLevelBlocks(html: string): string[] {
  const blocks: string[] = [];
  const tagPattern = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)([^>]*)>/g;
  // Void elements never open a depth level.
  const voidTags = new Set(['br', 'hr', 'img', 'input', 'meta', 'link', 'source', 'track', 'wbr']);

  let depth = 0;
  let blockStart = 0;
  let match: RegExpExecArray | null;

  while ((match = tagPattern.exec(html)) !== null) {
    const [full, closing, rawName, attrs] = match;
    const name = rawName.toLowerCase();
    if (voidTags.has(name) || attrs.trim().endsWith('/')) continue;

    if (closing) {
      depth -= 1;
      if (depth <= 0) {
        const end = match.index + full.length;
        blocks.push(html.slice(blockStart, end));
        blockStart = end;
        depth = 0;
      }
    } else {
      depth += 1;
    }
  }

  const tail = html.slice(blockStart);
  if (tail.trim()) blocks.push(tail);

  return blocks.filter((block) => block.trim());
}

function processArticle(html: string): ProcessedArticle {
  const toc: TocGroupItem[] = [];
  let currentGroup: TocGroupItem | null = null;
  let index = 0;

  const processed = html.replace(
    /<(h[2-6])([^>]*)>([\s\S]*?)<\/\1>/gi,
    (match, tag: string, attrs: string, inner: string) => {
      const text = stripTags(inner);
      if (!text) return match;

      const existingId = /id="([^"]*)"/i.exec(attrs);
      let id = existingId?.[1] ?? '';
      let outAttrs = attrs;
      if (!id) {
        id = slugifyHeading(text, index);
        outAttrs = `${attrs} id="${id}"`;
      }
      index += 1;

      if (tag.toLowerCase() === 'h2') {
        currentGroup = { id, text, children: [] };
        toc.push(currentGroup);
      } else if (currentGroup) {
        currentGroup.children.push({ id, text });
      }

      return `<${tag}${outAttrs}>${inner}</${tag}>`;
    },
  );

  return { html: processed, toc };
}

@Component({
  selector: 'app-blog-detail-page',
  imports: [
    Breadcrumb,
    RouterLink,
    TrustedCmsHtmlPipe,
    AssetUrlPipe,
    ReactiveFormsModule,
    CouponCard,
    ProductCard,
    SectionHeading,
    EventTopStores,
  ],
  providers: [NewsletterSignup],
  templateUrl: './blog-detail-page.html',
})
export class BlogDetailPage {
  /** In-article newsletter callout. Addresses land in the admin panel under Newsletter Subscribers. */
  protected readonly signup = inject(NewsletterSignup);
  private readonly affiliate = inject(AffiliateLinkService);

  private readonly api = inject(SavingArenaApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);
  protected readonly post = signal<FrontendBlog | null>(null);

  /**
   * Home / Blog / <category> / <article>.
   *
   * The two halves used to disagree: the visible nav ran Home / Blog / <category> and stopped, while
   * the BreadcrumbList ran Home / Blog / <article> with no category in it. This is both — the
   * category is a real step between the blog index and the piece, and the article is the page you
   * are on.
   */
  protected readonly crumbs = computed(() => {
    const categoryName = this.categoryName();
    const categorySlug = this.categorySlug();
    const postName = this.post()?.name || '';

    return breadcrumbTrail(
      { label: 'Blog', path: '/blogs' },
      ...(categoryName
        ? [{ label: categoryName, ...(categorySlug ? { path: `/blogs/${categorySlug}` } : {}) }]
        : []),
      ...(postName ? [{ label: postName }] : []),
    );
  });
  protected readonly isLoading = signal(true);
  protected readonly notFound = signal(false);

  /** Every published author, for the "SavingArena Heroes" carousel. */
  protected readonly heroAuthors = signal<HeroAuthor[]>([]);
  protected readonly canScrollHeroesPrev = signal(false);
  protected readonly canScrollHeroesNext = signal(false);

  @ViewChild('heroesTrack') private readonly heroesTrack?: ElementRef<HTMLElement>;

  protected readonly activeTocId = signal<string>('');

  private readonly processedArticle = computed<ProcessedArticle>(() => {
    const content = this.post()?.content;
    return content ? processArticle(String(content)) : { html: '', toc: [] };
  });

  protected readonly articleHtml = computed(() => this.processedArticle().html);

  protected readonly contentWidgets = computed<FrontendBlogContentWidget[]>(
    () => this.post()?.contentWidgets ?? [],
  );

  /**
   * The article body with its widgets interleaved.
   *
   * A widget's `position` is the number of article blocks rendered before it. A position past the
   * end of the article is clamped to the end rather than dropped, so trimming an article never
   * silently loses a widget the admin still expects to see.
   */
  protected readonly contentBlocks = computed<ArticleBlock[]>(() => {
    const html = this.articleHtml();
    if (!html) return [];

    const blocks = splitTopLevelBlocks(html);
    const widgets = this.contentWidgets();

    if (!widgets.length) {
      return blocks.map((block, index) => ({
        kind: 'html' as const,
        id: `html-${index}`,
        html: block,
      }));
    }

    const byPosition = new Map<number, FrontendBlogContentWidget[]>();
    for (const widget of widgets) {
      const position = Math.min(Math.max(0, widget.position ?? 0), blocks.length);
      const bucket = byPosition.get(position);
      if (bucket) bucket.push(widget);
      else byPosition.set(position, [widget]);
    }

    const output: ArticleBlock[] = [];
    const pushWidgetsAt = (position: number) => {
      let run: FrontendBlogContentWidget[] = [];
      const flushRun = () => {
        if (run.length > 1) {
          output.push({ kind: 'products', id: `products-${run[0].id}`, widgets: run });
        } else if (run.length === 1) {
          output.push({ kind: 'widget', id: `widget-${run[0].id}`, widget: run[0] });
        }
        run = [];
      };
      for (const widget of byPosition.get(position) ?? []) {
        // Only widgets whose product actually resolved join a grid; a dead one renders nothing
        // anyway, and counting it would turn a lone live card into a one-item "grid".
        if (widget.type === 'product' && widget.product) {
          run.push(widget);
          continue;
        }
        flushRun();
        output.push({ kind: 'widget', id: `widget-${widget.id}`, widget });
      }
      flushRun();
    };

    pushWidgetsAt(0);
    blocks.forEach((block, index) => {
      output.push({ kind: 'html', id: `html-${index}`, html: block });
      pushWidgetsAt(index + 1);
    });

    return output;
  });

  /** Maps a widget's coupon onto the shared coupon card's view model. */
  protected couponCardView(widget: FrontendBlogContentWidget): CouponCardView | null {
    const coupon = widget.coupon;
    if (!coupon) return null;

    return toCouponCardView(coupon, {
      assetBaseUrl: this.apiConfig.assetBaseUrl,
      fallbackTitle: widget.title || this.title(),
      fallbackStoreName: coupon.store?.name ?? '',
    });
  }

  /**
   * The same two-tab open the store and home pages use, so a coupon behaves identically wherever it
   * is clicked: a new tab gets `/<store>/coupon/<id>` (which renders the popup), and this tab
   * follows the `/go/...` redirect to the merchant.
   *
   * Order matters — the new tab must open while the click still counts as a user gesture.
   */
  protected onWidgetCouponAction(widget: FrontendBlogContentWidget): void {
    const coupon = widget.coupon;
    const storeSlug = coupon?.store?.slug ?? '';
    const id = coupon?.id ?? '';
    if (!coupon || !storeSlug || !id) return;

    if (!this.isBrowser) {
      this.router.navigate(['/', storeSlug, 'coupon', id]);
      return;
    }

    this.affiliate.openCoupon({
      storeSlug,
      couponId: id,
      store: coupon?.store?.name || storeSlug,
      kind: 'blog-widget',
    });
  }
  protected readonly tocGroups = computed(() => this.processedArticle().toc);

  protected readonly title = computed(() => this.post()?.name ?? '');
  protected readonly categoryName = computed(() => this.post()?.category?.name ?? 'Blog');
  protected readonly categorySlug = computed(() => this.post()?.category?.slug ?? '');
  protected readonly thumbnailUrl = computed(() => this.post()?.thumbnailImagePath ?? '');
  protected readonly authorName = computed(() => {
    const author = this.post()?.['author'];
    return typeof author === 'string' && author.trim() ? author.trim() : 'SavingArena Editorial';
  });
  /**
   * The person who actually wrote the article, from the detail payload's `authorProfile` (the
   * admin in the blog's `createdBy`). The card used to show a hardcoded "SavingArena editorial
   * team" paragraph next to a generated ui-avatars image, because the API served `author: ''` —
   * the blog detail endpoint was the one place that never populated `createdBy`.
   */
  protected readonly articleAuthor = computed<ArticleAuthor | null>(() => {
    const profile = this.post()?.authorProfile;
    if (!profile) return null;

    const name = (profile.name ?? '').trim();
    if (!name) return null;

    return {
      name,
      role: profile.designation ?? '',
      // Admin rich text: the card renders it as plain text, capped at the same 70 words the store
      // page's editor card uses.
      bio: truncateWords(stripTags(profile.shortDescription || profile.bio || ''), 70),
      avatarPath: (profile.profileImagePath ?? '').trim(),
      initials: authorInitials(name),
      profileUrl: (profile.profileUrl ?? '').trim(),
    };
  });

  protected readonly publishedLabel = computed(() => this.formatDate(this.post()?.publishAt));
  protected readonly updatedLabel = computed(() => this.formatDate(this.post()?.updatedAt));
  protected readonly viewsLabel = computed(() => {
    const views = Number(this.post()?.views ?? 0);
    return views > 0 ? `${views.toLocaleString('en-US')} views` : '';
  });
  protected readonly readTime = computed(() => {
    const post = this.post();
    return post ? blogReadMinutes(post) : 1;
  });
  protected readonly faqItems = computed(() => this.post()?.faqs ?? []);

  /**
   * "Alternative Stores" — the stores an admin assigned to this post, each linking to its store
   * page. Assignment is the only source: a post with no stores hides the section rather than
   * falling back to unrelated brands.
   */
  protected readonly relatedStores = computed<RelatedStore[]>(() =>
    (this.post()?.stores ?? [])
      .filter((store) => store.name && store.slug)
      .slice(0, RELATED_STORE_LIMIT)
      .map((store) => ({
        id: store.id,
        name: store.name,
        path: `/${store.slug}`,
        domain: store.domain ?? '',
        // Admin copy is rich text; these cards render it as plain text, so strip the tags.
        description: stripHtml(
          this.readString(store, 'shortDescription') || this.readString(store, 'description'),
        ),
        logoUrl: store.logoImagePath ?? '',
      })),
  );

  /** `FrontendStore` types only its core fields; the rest arrive through its index signature. */
  private readString(store: FrontendStore, key: string): string {
    const value = store[key];
    return typeof value === 'string' ? value.trim() : '';
  }

  /** Cards in the "Related Blogs" strip, resolved by silo → category → recency server-side. */
  protected readonly relatedPosts = signal<BlogPost[]>([]);

  /** Approved comments for this article. Was a hardcoded pair of invented readers. */
  protected readonly comments = signal<FrontendBlogComment[]>([]);
  /** Which comment's reply box is open, by id. */
  protected readonly replyingToCommentId = signal<string>('');
  protected readonly commentSubmitting = signal(false);
  protected readonly commentError = signal('');
  /** Set after a successful submit — the comment is queued for moderation, not live yet. */
  protected readonly commentQueued = signal(false);

  constructor() {
    // `getBlog` increments the server-side view count, so it belongs ONLY here on the detail page.
    // Running it in the switchMap ties one request to each slug and, on the server, holds rendering
    // until the post resolves — so the article HTML + SEO tags are in the initial response (spec §5/§9).
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          const slug = params.get('slug') ?? '';
          this.isLoading.set(true);
          this.notFound.set(false);
          this.post.set(null);
          return this.api.getBlog(slug).pipe(
            catchError((error: unknown) => {
              this.ssrResponse.noteFetchFailure(error);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.isLoading.set(false);
        const post = response?.success ? response.data : null;
        this.post.set(post);
        this.applyBlogSeo(post);
        const firstGroup = this.tocGroups()[0];
        if (firstGroup) this.activeTocId.set(firstGroup.id);

        // Fetched separately, after the article resolves: comments are below the fold and a slow or
        // failed comments call must never hold up the article's SSR render.
        this.commentQueued.set(false);
        this.commentError.set('');
        this.replyingToCommentId.set('');
        this.loadComments(post?.slug ?? '');
        this.loadRelatedPosts(post?.slug ?? '');
      });

    this.loadHeroAuthors();
  }

  /**
   * Loads the author strip. Independent of the article request, so a slow or failed author lookup
   * never delays the post itself — the strip simply stays hidden.
   */
  private loadHeroAuthors(page = 1, accumulated: FrontendUser[] = []): void {
    this.api
      .getUsers({ page, limit: 100 })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response) {
          this.heroAuthors.set(this.toHeroAuthors(accumulated));
          return;
        }

        const users = [...accumulated, ...response.data];

        if (response.pagination.page < response.pagination.pages) {
          this.loadHeroAuthors(response.pagination.page + 1, users);
          return;
        }

        this.heroAuthors.set(this.toHeroAuthors(users));
        // The track only has a width once the cards render.
        setTimeout(() => this.onHeroesScroll());
      });
  }

  /**
   * Scrolls the author carousel by one visible page. Native scrolling does the work, so the strip
   * stays swipeable on touch and keyboard-reachable without the arrows.
   */
  protected scrollHeroes(direction: 1 | -1): void {
    const track = this.heroesTrack?.nativeElement;
    if (!track) return;

    track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
  }

  /** Arrows are disabled at each end instead of scrolling into empty space. */
  protected onHeroesScroll(): void {
    const track = this.heroesTrack?.nativeElement;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    this.canScrollHeroesPrev.set(track.scrollLeft > 4);
    this.canScrollHeroesNext.set(track.scrollLeft < maxScroll - 4);
  }

  private toHeroAuthors(users: FrontendUser[]): HeroAuthor[] {
    return users
      .filter((user) => user.status !== 'inactive' && user.slug && user.name)
      .map((user) => ({
        slug: user.slug,
        name: user.name,
        role: stripTags(user.designation || '') || 'Author',
        imageUrl: user.profileImagePath || '',
        profilePath: `/experts/${user.slug}`,
      }));
  }

  private applyBlogSeo(post: FrontendBlog | null): void {
    if (!post) {
      // An unknown/unpublished slug is a real 404, not a 200 "not found" screen (spec §7).
      this.notFound.set(true);
      this.ssrResponse.setNotFound();
      this.seo.apply({
        title: 'Article not found | SavingArena',
        description: 'The article you are looking for is not available on SavingArena.',
        noindex: true,
      });
      this.seo.setJsonLd([]);
      return;
    }

    const path = `/blogs/${post.slug}`;
    const url = this.seo.absolute(path);
    const description =
      (post.shortDescription && post.shortDescription.trim()) ||
      stripTags(String(post.content ?? '')).slice(0, 160);
    const image = post.thumbnailImagePath || undefined;
    const faqs = this.faqItems();

    this.seo.apply({
      title: `${post.name} | SavingArena`,
      description,
      canonicalPath: path,
      image,
      type: 'article',
    });

    this.seo.setJsonLd([
      articleSchema({
        origin: this.seo.origin,
        url,
        headline: post.name,
        description,
        image: image ? this.seo.absolute(image) : undefined,
        authorName: this.authorName(),
        datePublished: post.publishAt || undefined,
        // Unlike the mock, the live payload carries a real updatedAt — safe to emit.
        dateModified: post.updatedAt || undefined,
      }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
      // FAQPage only when the article actually renders FAQs (spec §8).
      faqs.length
        ? faqPageSchema(faqs.map((faq) => ({ question: faq.question, answer: faq.answer })))
        : null,
    ]);
  }

  /** Total shown in the section heading — top-level comments plus their replies. */
  protected readonly commentCount = computed(() =>
    this.comments().reduce((total, comment) => total + 1 + comment.replies.length, 0),
  );

  protected toggleReplyForm(commentId: string): void {
    this.commentError.set('');
    this.replyingToCommentId.update((current) => (current === commentId ? '' : commentId));
  }

  /**
   * Posts a comment, or a reply when `parentId` is given.
   *
   * Nothing is added to the list on success: the comment is pending until an admin approves it, so
   * showing it immediately would tell the visitor it is live when it is not.
   */
  protected submitComment(author: string, message: string, parentId = ''): void {
    if (this.commentSubmitting()) return;

    const authorValue = author.trim();
    const messageValue = message.trim();
    this.commentError.set('');
    this.commentQueued.set(false);

    if (!authorValue || !messageValue) {
      this.commentError.set('Add your name and a comment first.');
      return;
    }
    if (containsUrl(authorValue) || containsUrl(messageValue)) {
      this.commentError.set('Links are not allowed in comments.');
      return;
    }

    const slug = this.post()?.slug ?? '';
    if (!slug) return;

    this.commentSubmitting.set(true);
    this.api
      .submitBlogComment(slug, {
        authorName: authorValue,
        message: messageValue,
        ...(parentId ? { parentId } : {}),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.commentSubmitting.set(false);
          this.commentQueued.set(true);
          this.replyingToCommentId.set('');
        },
        error: (err: unknown) => {
          this.commentSubmitting.set(false);
          const status = (err as { status?: number }).status;
          const serverMessage = (err as { error?: { message?: string } }).error?.message;
          this.commentError.set(
            status === 422 && serverMessage
              ? serverMessage
              : 'Could not post your comment right now. Please try again.',
          );
        },
      });
  }

  /**
   * The "Related Blogs" strip. Was four invented articles with stock photography and links that
   * 404'd (`/blogs/saving-strategy`, `/experts/aisha-rahman`). The backend picks these by silo,
   * then category, then recency, so the strip is always full and always real.
   */
  private loadRelatedPosts(slug: string): void {
    if (!slug) {
      this.relatedPosts.set([]);
      return;
    }

    this.api
      .getRelatedBlogs(slug, RELATED_POST_LIMIT)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const blogs = response?.success ? (response.data ?? []) : [];
        this.relatedPosts.set(toBlogPosts(blogs, this.apiConfig.assetBaseUrl));
      });
  }

  private loadComments(slug: string): void {
    if (!slug) {
      this.comments.set([]);
      return;
    }

    this.api
      .getBlogComments(slug)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.comments.set(response?.success ? (response.data ?? []) : []);
      });
  }

  /**
   * Callout/CTA button URLs are typed by an admin, so they can be an in-app path (`/coupons`) or an
   * off-site link. The template routes the first client-side and opens the second in a new tab.
   */
  protected isExternalUrl(url: string): boolean {
    return /^https?:\/\//i.test((url || '').trim());
  }

  /** "Aug 19, 2026" for a comment's ISO timestamp; empty when the date is missing or unparseable. */
  protected commentDate(value: string | undefined): string {
    return this.formatDate(value);
  }

  protected scrollToHeading(event: Event, id: string): void {
    event.preventDefault();
    this.activeTocId.set(id);
    // Click handlers only run in the browser, so `document` is safe here.
    const target = document.getElementById(id);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Avatar initials for the hero byline and every comment — one source, so they always agree. */
  protected initials(name: string): string {
    return authorInitials(name);
  }

  private formatDate(value: unknown): string {
    if (typeof value !== 'string' || !value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(parsed);
  }

}
